"use client";

import { Core } from "@faire/design-tokens";
import { ScrollWrapper } from "@faire/retailer-visitor-shared/components/Scroll/ScrollWrapper";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import {
  IconButton,
  StyledIconWrapper,
} from "@faire/web--source/slate/IconButton/IconButton";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { observer } from "mobx-react";
import * as React from "react";
import { useState } from "react";
import styled from "styled-components";

const BUTTON_WIDTH = 35;
export const ROUNDING_THRESHOLD = 1;
enum Direction {
  NEXT = "next",
  PREVIOUS = "previous",
}

const BaseFiniteScrollCarouselBase: React.FC<
  BaseFiniteScrollCarousel.IProps
> = ({
  chevronButtonDimension,
  children,
  className,
  enableDrag,
  forwardRef,
  name,
  onLeftEndReached,
  onRightEndReached,
  onVisible,
  peekGapPercentage = 25,
  peekGapPixel = 0,
  scrollConfiguration,
  shouldHideLeftButton,
  shouldHideRightButton,
  useIconArrows,
  iconArrowsOffsetTop,
}) => {
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isRightEndReached, setIsRightEndReached] = useState(false);
  const [isLeftEndReached, setIsLeftEndReached] = useState(true);
  const [mouseDownRelativeXCoordinate, setMouseDownRelativeXCoordinate] =
    useState<number | undefined>();
  const [scrollLeft, setScrollLeft] = useState<number | undefined>();
  const { strictLocalize } = useStrictLocalization();

  const scrollRef = forwardRef;

  const handleMouseDown = (isDown: boolean) => {
    if (!enableDrag) {
      return;
    }
    setIsMouseDown(isDown);
  };

  const onMouseDown = (event: React.MouseEvent) => {
    if (!enableDrag) {
      return;
    }

    const currentElement = scrollRef.current;
    if (!currentElement) {
      return;
    }
    handleMouseDown(true);
    const { x } = currentElement.getBoundingClientRect();
    // record starting x coordinate relative to carousel
    setMouseDownRelativeXCoordinate(event.pageX - x);
    setScrollLeft(currentElement.scrollLeft);
  };

  const onMouseMove = (event: React.MouseEvent) => {
    if (!enableDrag) {
      return;
    }
    if (!isMouseDown) {
      return;
    }
    const currentElement = scrollRef.current;
    if (
      !currentElement ||
      mouseDownRelativeXCoordinate === undefined ||
      scrollLeft === undefined
    ) {
      return;
    }
    event.preventDefault();
    const { x } = currentElement.getBoundingClientRect();
    // calculate mouse x coordinate relative to the carousel
    const mouseRelativeXCoordinate = event.pageX - x;
    // calculate distance mouse moved
    const movingDistance =
      mouseRelativeXCoordinate - mouseDownRelativeXCoordinate;

    // scroll carousel based on the distance mouse moved
    currentElement.scrollTo?.({
      left: scrollLeft - movingDistance,
    });
  };

  const onScroll = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const { width } = element.getBoundingClientRect();
    // since scrollLeft and scrollWidth are calculated as integers we subtract ROUNDING_THRESHOLD to account for errors
    const isRightEndReachedAfterScroll =
      element.scrollLeft + Math.ceil(width) >=
      element.scrollWidth - ROUNDING_THRESHOLD;
    if (isRightEndReachedAfterScroll && !isRightEndReached) {
      trackEvent(`/${name}/finite-scroll-carousel/last-item`);
    }
    setIsRightEndReached(isRightEndReachedAfterScroll);
    setIsLeftEndReached(!element?.scrollLeft);
    onRightEndReached?.(isRightEndReachedAfterScroll);
    onLeftEndReached?.(!element?.scrollLeft);
  };

  const renderButton = () => {
    const buttonHeight = chevronButtonDimension?.height.toString();
    const buttonWidth = chevronButtonDimension?.width.toString();
    return (
      <>
        {shouldHideLeftButton ? null : (
          <PreviousButton
            onClick={onClick(Direction.PREVIOUS)}
            data-test-id="finite-scroll-btn-previous"
            aria-label={strictLocalize({
              defaultMessage: "Previous Carousel Slide",
              description: {
                text: "Aria label for the previous carousel slide button",
              },
            })}
            disabled={isLeftEndReached}
            $isDeadEndReached={isLeftEndReached}
          >
            <Chevron
              direction="left"
              color="inherit"
              height={buttonHeight}
              width={buttonWidth}
            />
          </PreviousButton>
        )}
        {shouldHideRightButton ? null : (
          <NextButton
            onClick={onClick(Direction.NEXT)}
            data-test-id="finite-scroll-btn-next"
            aria-label={strictLocalize({
              defaultMessage: "Next Carousel Slide",
              description: {
                text: "Aria label for the next carousel slide button",
              },
            })}
            disabled={isRightEndReached}
            $isDeadEndReached={isRightEndReached}
          >
            <Chevron
              direction="right"
              color="inherit"
              height={buttonHeight}
              width={buttonWidth}
            />
          </NextButton>
        )}
      </>
    );
  };

  const renderIconButtons = () => {
    return (
      <>
        {shouldHideLeftButton ? null : (
          <PreviousIconButton
            onClick={onClick(Direction.PREVIOUS)}
            data-test-id="finite-scroll-btn-previous"
            aria-label={strictLocalize({
              defaultMessage: "Previous Carousel Slide",
              description: {
                text: "Aria label for the previous carousel slide button",
              },
            })}
            variant="tertiary"
            size="small"
            disabled={isLeftEndReached}
            offsetTop={iconArrowsOffsetTop}
          >
            <Chevron direction="left" />
          </PreviousIconButton>
        )}
        {shouldHideRightButton ? null : (
          <NextIconButton
            onClick={onClick(Direction.NEXT)}
            data-test-id="finite-scroll-btn-next"
            aria-label={strictLocalize({
              defaultMessage: "Next Carousel Slide",
              description: {
                text: "Aria label for the next carousel slide button",
              },
            })}
            variant="tertiary"
            size="small"
            disabled={isRightEndReached}
            offsetTop={iconArrowsOffsetTop}
          >
            <Chevron direction="right" />
          </NextIconButton>
        )}
      </>
    );
  };

  const onClick = (direction: Direction) => () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const { width } = element.getBoundingClientRect();

    let left = element.scrollLeft;

    const peekGapPercentageToPixel = (peekGapPercentage * width) / 100;
    const movePixel = scrollConfiguration
      ? scrollConfiguration.numTilesToMove *
        (scrollConfiguration.tileWidth + scrollConfiguration.gapWidth)
      : width - peekGapPixel - peekGapPercentageToPixel;

    if (direction === Direction.NEXT) {
      left += movePixel;
    } else if (direction === Direction.PREVIOUS) {
      left -= movePixel;
    }

    trackEvent(`/${name}/finite-scroll-carousel/${direction}`);

    element.scrollTo?.({
      left,
      behavior: "smooth",
    });
  };

  return (
    <Wrapper className={className} ref={onVisible}>
      <StyledScrollWrapper
        $horizontal
        onMouseDown={onMouseDown}
        onMouseLeave={() => handleMouseDown(false)}
        onMouseUp={() => handleMouseDown(false)}
        onMouseMove={onMouseMove}
        onScroll={onScroll}
        ref={scrollRef}
        data-test-id="finite-scroll-wrapper"
      >
        {children}
      </StyledScrollWrapper>
      {useIconArrows ? renderIconButtons() : renderButton()}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */
const Button = styled.button<{ $isDeadEndReached: boolean }>`
  position: absolute;
  top: calc(50% - ${BUTTON_WIDTH / 2}px);
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDeadEndReached }) =>
    $isDeadEndReached ? "default" : "pointer"};

  ${responsiveMediaQueries.mobile} {
    display: none;
  }
`;

const PreviousButton = styled(Button)`
  left: -${BUTTON_WIDTH / 2}px;
`;

const NextButton = styled(Button)`
  right: -${BUTTON_WIDTH / 2}px;
`;

const StyledIconButton = styled(IconButton)<{
  offsetTop?: string;
  arrowColor?: string;
}>`
  position: absolute;
  top: ${(props) => props.offsetTop ?? "50%"};
  ${StyledIconWrapper} {
    svg {
      stroke: ${(props) => props.arrowColor};
    }
    &:hover {
      svg {
        stroke: ${Core.icon.default};
      }
    }
  }
`;

const PreviousIconButton = styled(StyledIconButton)`
  left: 0;
  transform: translate(calc((100% + 8px) * -1), -50%);
`;

const NextIconButton = styled(StyledIconButton)`
  right: 0;
  transform: translate(calc(100% + 8px), -50%);
`;

const StyledScrollWrapper = styled(ScrollWrapper)`
  overflow-y: hidden;
  &&& {
    display: flex;
  }
  scroll-snap-type: x mandatory;
  // prevent scrollbar show up
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export namespace BaseFiniteScrollCarousel {
  export const BaseFiniteScrollCarousel = observer(
    BaseFiniteScrollCarouselBase
  );
  export const Next = NextButton;
  export const NextIcon = NextIconButton;
  export const Previous = PreviousButton;
  export const PreviousIcon = PreviousIconButton;
  export const Container = Wrapper;
  export const Scrollable = StyledScrollWrapper;
  export interface ScrollConfiguration {
    numTilesToMove: number;
    tileWidth: number;
    gapWidth: number;
  }
  export interface IProps {
    children?: React.ReactNode | undefined;
    forwardRef: React.MutableRefObject<HTMLDivElement | null>;
    shouldHideLeftButton?: boolean;
    onLeftEndReached?: (isLeftEndReached: boolean) => void;
    shouldHideRightButton?: boolean;
    onRightEndReached?: (isRightEndReached: boolean) => void;
    name: string;
    className?: string;
    onScroll?: () => void;
    onVisible?: (instance: Element | null | undefined) => void;
    chevronButtonDimension?: { width: number; height: number };
    enableDrag?: boolean;
    peekGapPixel?: number;
    peekGapPercentage?: number;
    scrollConfiguration?: ScrollConfiguration;
    useIconArrows?: boolean;
    iconArrowsOffsetTop?: string;
  }
}
